import { initShaderProgram, IShaderProgramInfo } from "../Shader";
import { Camera } from "../Camera";
import { mat4 } from "gl-matrix";
import { IRGBA, setBeginningDay, differenceInDays } from "../TimelineUtils";
import { IBaseTime } from "src/redux/reducers/CrewScheduleReducer";
import { getLocalTimeMapAt } from "./BaseTimelineLabelsGL";

interface IGLAttributes {
  position: WebGLBuffer;
}

const oneDay = 86400000;

export class TimeAxisLines {
  // Vertex shader program
  private readonly vsSource = `
    attribute vec4 aVertexPosition;

    uniform mat4 uModelViewMatrix;
    uniform mat4 uProjectionMatrix;
    uniform vec4 uColor;

    varying highp float vYPos;
    varying lowp vec4 vColor;

    void main(void) {
      gl_Position = uProjectionMatrix * uModelViewMatrix * aVertexPosition;
      gl_Position.y = aVertexPosition.y;
      vColor = uColor;
      vYPos = aVertexPosition.y;
    }
  `;
  // Fragment shader program
  private readonly fsSource = `
    varying highp float vYPos;
    varying lowp vec4 vColor;
    void main(void) {
      if(mod(vYPos, 0.02) > 0.01) {
        discard;
      }
      gl_FragColor = vColor;

    }
  `;

  private shaderProgram: WebGLProgram;
  private programInfo: IShaderProgramInfo;
  private buffers: IGLAttributes;
  private positions: number[] = [];
  private modelViewMatrix: mat4;

  constructor(gl: WebGLRenderingContext) {
    // Initialize a shader program; this is where all the lighting
    // for the vertices and so forth is established.
    this.shaderProgram = initShaderProgram(gl, this.vsSource, this.fsSource);

    // Collect all the info needed to use the shader program.
    // Look up which attribute our shader program is using
    // for aVertexPosition and look up uniform locations.
    this.programInfo = {
      program: this.shaderProgram,
      attribLocations: {
        vertexPosition: gl.getAttribLocation(this.shaderProgram, "aVertexPosition"),
      },
      uniformLocations: {
        projectionMatrix: gl.getUniformLocation(this.shaderProgram, "uProjectionMatrix"),
        modelViewMatrix: gl.getUniformLocation(this.shaderProgram, "uModelViewMatrix"),
        color: gl.getUniformLocation(this.shaderProgram, "uColor"),
      },
    };

    // Set the drawing position to the "identity" point, which is
    // the center of the scene.
    this.modelViewMatrix = mat4.create();

    // Now move the drawing position a bit to where we want to
    // start drawing the square.

    mat4.translate(
      this.modelViewMatrix, // destination matrix
      this.modelViewMatrix, // matrix to translate
      [0.0, 0.0, -2.0]
    ); // amount to translate
    // Here's where we call the routine that builds all the
    // objects we'll be drawing.
    this.buffers = this.initBuffers(gl);
  }

  //
  // initBuffers
  //
  // Initialize the buffers we'll need. For this demo, we just
  // have one object -- a simple two-dimensional square.
  //
  private initBuffers = (gl: WebGLRenderingContext): IGLAttributes => {
    // Create a buffer for the square's positions.
    const positionBuffer = gl.createBuffer();

    // Select the positionBuffer as the one to apply buffer
    // operations to from here out.
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);

    // Now create an array of positions for the square.
    this.positions = [];
    // Now pass the list of positions into WebGL to build the
    // shape. We do this by creating a Float32Array from the
    // JavaScript array, then use it to fill the current buffer.

    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.positions), gl.DYNAMIC_DRAW);

    return {
      position: positionBuffer,
    };
  };

  public updateBuffers = (gl: WebGLRenderingContext, timeAxisRange: number[], baseTime: IBaseTime): void => {
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);
    this.positions = baseTime.isUTC
      ? this.getUTCPositions(timeAxisRange)
      : this.getLocalPositions(timeAxisRange, baseTime);

    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.positions), gl.DYNAMIC_DRAW);
  };

  private getUTCPositions(timeAxisRange: number[]): number[] {
    const positions = [];
    const [minTime, maxTime] = timeAxisRange;
    const minDate = setBeginningDay(minTime).getTime();
    const maxDate = setBeginningDay(maxTime).getTime();

    const differenceDays = differenceInDays(minDate, maxDate);
    const numTicks = differenceDays < 90 ? differenceDays : 0;
    for (let i = 0; i < numTicks + 1; i++) {
      const time = minDate + i * oneDay;
      const x = time - Camera.referenceTime;
      const tickPositions = [x, -1.0, x, 1.0];
      positions.push(...tickPositions);
    }
    return positions;
  }

  private getLocalPositions(timeAxisRange: number[], baseTime: IBaseTime): number[] {
    const positions = [];
    const [minTime, maxTime] = timeAxisRange;

    const time = getLocalTimeMapAt(baseTime.timezoneId, minTime).localMoment.clone().startOf("day");

    while (time?.valueOf() <= maxTime) {
      time.add(1, "day");
      const x = time?.valueOf() - Camera.referenceTime;
      const tickPositions = [x, -1.0, x, 1.0];
      positions.push(...tickPositions);
    }
    return positions;
  }

  public draw = (gl: WebGLRenderingContext, camera: Camera, color: IRGBA): void => {
    if (this.positions.length < 4 || !color || !camera) {
      return;
    }

    // Tell WebGL how to pull out the positions from the position
    // buffer into the vertexPosition attribute.
    {
      const numComponents = 2;
      const type = gl.FLOAT;
      const normalize = false;
      const stride = 0;
      const offset = 0;
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);

      gl.vertexAttribPointer(
        this.programInfo.attribLocations.vertexPosition,
        numComponents,
        type,
        normalize,
        stride,
        offset
      );
      gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexPosition);
    }

    // Tell WebGL to use our program when drawing
    gl.useProgram(this.programInfo.program);

    // Set the shader uniforms
    const projectionMatrix = camera.getProjectionMatrix(gl);
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.projectionMatrix, false, projectionMatrix);
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.modelViewMatrix, false, this.modelViewMatrix);

    gl.uniform4f(this.programInfo.uniformLocations.color, color.red, color.green, color.blue, 1.0);

    {
      const offset = 0;
      gl.drawArrays(gl.LINES, offset, this.positions.length / 2);
    }
  };
}
